import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type ExperienceTicket } from '../services/api'
import { useStore } from '../store/useStore'
import { isExperienceEnabled } from '../lib/features'

/**
 * 現在ログイン中ユーザーの体験チケット一覧 (#1248 / #1283)。
 * useMyReservations と同じ軽量ポーリング方針だが、チケットは秒単位性が
 * 不要なので取得は mount 時 + 画面復帰時のみ。feature OFF / 未ログインは空。
 *
 * active  = 利用可能(issued/paid) と 支払い待ち(pending) — 「進行中」タブ相当
 * history = 使用済み(redeemed) と 決済失敗(failed)          — 「履歴」タブ相当
 */
export function useMyExperienceTickets() {
  const { user } = useStore()
  const enabled = isExperienceEnabled()
  const [tickets, setTickets] = useState<ExperienceTicket[]>([])
  const [loading, setLoading] = useState(!!user?.id && enabled)

  // ユーザー切替で state をリセット (derive-state-from-props)。
  const [trackedUserId, setTrackedUserId] = useState(user?.id)
  if (user?.id !== trackedUserId) {
    setTrackedUserId(user?.id)
    setTickets([])
    setLoading(!!user?.id && enabled)
  }

  const genRef = useRef(0)

  const fetchOnce = useCallback(async () => {
    if (!user?.id || !enabled) return
    const gen = ++genRef.current
    try {
      const res = await api.getMyExperienceTickets()
      if (gen !== genRef.current) return
      setTickets(res?.tickets ?? [])
    } catch (err) {
      if (gen !== genRef.current) return
      console.error('[useMyExperienceTickets] fetch error:', err)
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [user?.id, enabled])

  useEffect(() => {
    if (!user?.id || !enabled) return
    fetchOnce()
    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchOnce()
    }
    document.addEventListener('visibilitychange', onFocus)
    return () => document.removeEventListener('visibilitychange', onFocus)
  }, [user?.id, enabled, fetchOnce])

  const active = tickets.filter(
    (ti) => ti.paymentStatus === 'pending' || (ti.paymentStatus === 'paid' && ti.status === 'issued'),
  )
  const history = tickets.filter(
    (ti) => ti.status === 'redeemed' || ti.paymentStatus === 'failed'
      // #2298 取り消した券でも、返金に失敗した・別の方法で返したものは出す (サーバもこれだけ返す)
      || (ti.status === 'void' && (ti.refundStatus === 'failed' || ti.refundStatus === 'alternate')),
  )

  return { tickets, active, history, loading, refetch: fetchOnce }
}
